import { ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';

// Layouts
import { MainLayout } from '@/layout/main-layout/main-layout';
import { AuthLayout } from '@/layout/auth-layout/auth-layout';

// Auth Pages
import { SigninPage } from '@/modules/auth/pages/signin/signin-page';
import { ActivationSuccessPage } from '@/modules/auth/pages/activation-success';
import { EmailSentPage } from '@/modules/auth/pages/email-sent';
import { ForgotPasswordPage } from '@/modules/auth/pages/forgot-password';
import { AccountActivationPage } from '@/modules/auth/pages/account-activation';
import { SignupPage } from '@/modules/auth/pages/signup';
import { ResetPasswordPage } from '@/modules/auth/pages/reset-password';
import { VerifyOtpKeyPage } from '@/modules/auth/pages/verify-otp-key';
import { VerificationFailed } from '@/modules/auth/pages/verification-failed';
import { SigninOidcCallBackPage } from '@/modules/auth/pages/signin-oidc-callback';
import SignupEmailSentPage from '@/modules/auth/pages/signup-email-sent';
import { SsoActivationPage } from '@/modules/auth/pages/sso-activation/sso-activation';
import { SsoSignupPage } from '@/modules/auth/pages/sso-signup/sso-signup';

// App Pages
import AnalyticsPage from '@/modules/admin/analytics/pages/admin-analytics-page';
import AnalyticsSettingsPage from '@/modules/admin/analytics/pages/admin-analytics-settings-page';
import AdminDashboard from '@/modules/admin/pages/admin-dashboard-page';
import AdminLayoutPage from '@/modules/admin/pages/admin-layout-page';
import AdminSettingsPage from '@/modules/admin/settings/pages/admin-settings-page';
import AdminTemplateBuilderPage from '@/modules/admin/templates/pages/admin-template-builder-page';
import AdminTemplateClassicBuilderPage from '@/modules/admin/templates/pages/admin-template-classic-builder-page';
import AdminTemplateEditPage from '@/modules/admin/templates/pages/admin-template-edit-page';
import NewTemplatePage from '@/modules/admin/templates/pages/admin-templates-new-page';
import TemplatesAdminPage from '@/modules/admin/templates/pages/admin-templates-page';
import UsersAdminPage from '@/modules/admin/users/pages/admin-users-page';
import NotFoundPage from '@/modules/not-found/not-found-page';
import { OnboardingPage } from '@/modules/onboarding';
import { HomeRedirect } from '@/modules/onboarding/pages/home-redirect';
import {
  OrgDangerZonePage,
  OrgGeneralSettingsPage,
  OrgIntegrationsPage,
  OrgLayoutPage,
  OrgLetterheadsPage,
  OrgNewPage,
  OrgNotFoundPage,
  OrgOverviewPage,
  OrgSelectPage,
  OrgSettingsLayoutPage,
  OrgTemplateBuilderPage,
  OrgTemplateEditPage,
  OrgTemplateNewPage,
  OrgTemplatesPage,
  OrgUnauthorizedPage,
  OrgUsersPage,
} from '@/modules/org';
import { ProfilePage } from '@/modules/profile';
import { TaskManagerPage } from '@/modules/task-manager';
import AiTestPage from '@/modules/templates/pages/ai-test/AiTestPage';
import PaymentResultPage from '@/modules/payment/PaymentResultPage';
import TemplateGeneratePage from '@/modules/templates/pages/template-generate';
import TemplateReviewPage from '@/modules/templates/pages/template-review';
import TemplateReviewAwaitApprovalPage from '@/modules/templates/pages/template-review-await-approval';
import SignatureSuccessPage from '@/modules/templates/pages/template-review-success';
import OrgContractsPage from '@/modules/org/contracts/pages/org-contracts-page';
import OrgContactsManagerPage from '@/modules/org/contacts/pages/org-contacts-page';
import OrgCompaniesPage from '@/modules/org/companies/pages/org-companies-page';
import OrgArchivePage from '@/modules/org/archive/pages/org-archive-page';
import OrgTrashPage from '@/modules/org/trash/pages/org-trash-page';
import OrgContractCheckerPage from '@/modules/org/contract-checker/pages/org-contract-checker-page';
import OrgContractCheckerNewPage from '@/modules/org/contract-checker/pages/org-contract-checker-new-page';
import OrgContractCheckerResultPage from '@/modules/org/contract-checker/pages/org-contract-checker-result-page';
import OrgDocxTemplateNewPage from '@/modules/org/pages/org-docx-template-new-page';
import OrgTemplateDocxBuilderPage from '@/modules/org/pages/org-template-docx-builder-page';
import DocxTemplateGeneratePage from '@/modules/templates/pages/docx-template-generate';

export interface AppRoute {
  path?: string;
  index?: boolean;
  element?: ReactNode;
  children?: AppRoute[];
  isPrivate?: boolean;
}

const DocxReviewRedirect = () => {
  const { slug } = useParams();
  const location = useLocation();
  return <Navigate to={`/templates/${slug}/review${location.search}`} replace />;
};

export const routesConfig: AppRoute[] = [
  // --- AUTHENTICATION ROUTES (Public) ---
  {
    element: <AuthLayout />,
    isPrivate: false,
    children: [
      { path: '/login', element: <SigninPage /> },
      { path: '/oidc', element: <SigninOidcCallBackPage /> },
      { path: '/signup', element: <SignupPage /> },
      { path: '/sent-email', element: <EmailSentPage /> },
      { path: '/activate', element: <AccountActivationPage /> },
      { path: '/sso-signup', element: <SsoSignupPage /> },
      { path: '/sso/:provider/callback', element: <SsoActivationPage /> },
      { path: '/resetpassword', element: <ResetPasswordPage /> },
      { path: '/success', element: <ActivationSuccessPage /> },
      { path: '/activate-failed', element: <VerificationFailed /> },
      { path: '/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/verify-mfa', element: <VerifyOtpKeyPage /> },
      { path: '/signup-email-sent', element: <SignupEmailSentPage /> },
    ],
  },

  // --- MAIN APPLICATION ROUTES (Private) ---
  {
    element: <MainLayout />,
    isPrivate: true,
    children: [
      { path: '/', element: <HomeRedirect /> },
      { path: '/profile', element: <ProfilePage /> },
      { path: '/task-manager', element: <TaskManagerPage /> },
      { path: '/onboarding', element: <OnboardingPage /> },
      
      // Organization Routes
      { path: '/org/select', element: <OrgSelectPage /> },
      { path: '/org/not-found', element: <OrgNotFoundPage /> },
      { path: '/org/unauthorized', element: <OrgUnauthorizedPage /> },
      { path: '/org/new', element: <OrgNewPage /> },
      {
        path: '/org/:orgId',
        element: <OrgLayoutPage />,
        children: [
          { index: true, element: <OrgOverviewPage /> },
          { path: 'users', element: <OrgUsersPage /> },
          { path: 'contracts', element: <OrgContractsPage /> },
          { path: 'contacts', element: <OrgContactsManagerPage /> },
          { path: 'companies', element: <OrgCompaniesPage /> },
          { path: 'contract-checker', element: <OrgContractCheckerPage /> },
          { path: 'contract-checker/new', element: <OrgContractCheckerNewPage /> },
          { path: 'contract-checker/result/:id', element: <OrgContractCheckerResultPage /> },
          { path: 'archive', element: <OrgArchivePage /> },
          { path: 'trash', element: <OrgTrashPage /> },
          { path: 'templates', element: <OrgTemplatesPage /> },
          { path: 'templates/new', element: <OrgTemplateNewPage /> },
          { path: 'templates/new/docx', element: <OrgDocxTemplateNewPage /> },
          { path: 'templates/:id/edit', element: <OrgTemplateEditPage /> },
          { path: 'templates/:id/docx', element: <OrgTemplateDocxBuilderPage /> },
          { path: 'templates/:id/builder', element: <OrgTemplateBuilderPage /> },
          {
            path: 'settings',
            element: <OrgSettingsLayoutPage />,
            children: [
              { index: true, element: <OrgGeneralSettingsPage /> },
              { path: 'general', element: <OrgGeneralSettingsPage /> },
              { path: 'integrations', element: <OrgIntegrationsPage /> },
              { path: 'letterheads', element: <OrgLetterheadsPage /> },
              { path: 'danger-zone', element: <OrgDangerZonePage /> },
            ],
          },
        ],
      },
      
      // Template Routes
      { path: '/templates/:slug/generate', element: <TemplateGeneratePage /> },
      { path: '/templates/:slug/generate-docx', element: <DocxTemplateGeneratePage /> },
      { path: '/templates/:slug/generate-docx/review', element: <DocxReviewRedirect /> },
      { path: '/templates/:slug/review', element: <TemplateReviewPage /> },
      { path: '/templates/:slug/review/approval', element: <TemplateReviewAwaitApprovalPage /> },
      { path: '/templates/:slug/review/success', element: <SignatureSuccessPage /> },

      // Admin Routes
      {
        path: '/admin',
        element: <AdminLayoutPage />,
        children: [
          { index: true, element: <AdminDashboard /> },
          { path: 'templates', element: <TemplatesAdminPage /> },
          { path: 'templates/new', element: <NewTemplatePage /> },
          { path: 'templates/:id/edit', element: <AdminTemplateEditPage /> },
          { path: 'templates/:id/builder', element: <AdminTemplateBuilderPage /> },
          { path: 'templates/:id/classic-builder', element: <AdminTemplateClassicBuilderPage /> },
          { path: 'analytics', element: <AnalyticsPage /> },
          { path: 'analytics/settings', element: <AnalyticsSettingsPage /> },
          { path: 'settings', element: <AdminSettingsPage /> },
          { path: 'users', element: <UsersAdminPage /> },
        ],
      },
      
      // Fallback
      { path: '*', element: <NotFoundPage /> },
    ],
  },

  // --- PUBLIC UNPROTECTED ROUTES ---
  { path: '/ai-test', element: <AiTestPage />, isPrivate: false },
  { path: '/payment-result', element: <PaymentResultPage />, isPrivate: false },
];
